import { useEffect, useRef } from "react";
import type { RealtimeChannel } from "@supabase/supabase-js";
import { getSupabase } from "./auth";

/**
 * Tiny wrapper around Supabase realtime channels.
 * The dashboard subscribes to a fixed set of public tables (see
 * ADMIN_API_CONTRACT § 3.9). Each table maps to a single channel; we expose
 * a hook so components can react to inserts/updates without owning lifecycle.
 *
 * Failure mode: if Supabase isn't configured we silently no-op. The pages
 * still poll their data, just without live nudge.
 */

type RTEvent = "INSERT" | "UPDATE" | "DELETE";

export interface RealtimePayload<T = Record<string, unknown>> {
  eventType: RTEvent;
  new: T;
  old: T;
  table: string;
  schema: string;
  commit_timestamp: string;
}

export type RealtimeTable =
  | "queue_jobs"
  | "events"
  | "drafts_pending"
  | "sends"
  | "pager_log";

export function useRealtime<T = Record<string, unknown>>(
  table: RealtimeTable,
  cb: (p: RealtimePayload<T>) => void,
  events: RTEvent[] = ["INSERT", "UPDATE"],
) {
  const cbRef = useRef(cb);
  cbRef.current = cb;

  useEffect(() => {
    const supabase = getSupabase();
    if (!supabase) return;
    let channel: RealtimeChannel | null = null;
    // Unique-per-mount name so Supabase doesn't return a SUBSCRIBED channel
    // from a previous call (which would reject any new .on() registration).
    const uniqueId = `${table}-${Math.random().toString(36).slice(2, 10)}`;
    try {
      channel = supabase.channel(`db-${uniqueId}`);
      for (const ev of events) {
        (channel as unknown as {
          on: (
            type: string,
            cfg: { event: string; schema: string; table: string },
            cb: (payload: unknown) => void,
          ) => unknown;
        }).on(
          "postgres_changes",
          { event: ev, schema: "public", table },
          (payload: unknown) => {
            cbRef.current(payload as RealtimePayload<T>);
          },
        );
      }
      channel.subscribe((status: string) => {
        if (status === "CHANNEL_ERROR" || status === "TIMED_OUT") {
          console.warn("[realtime] channel", table, "->", status);
        }
      });
    } catch (e) {
      console.warn("[realtime] subscribe failed", table, e);
    }
    return () => {
      if (channel) {
        try {
          supabase.removeChannel(channel);
        } catch {
          /* noop */
        }
      }
    };
    // events array is stable in practice; we don't depend on it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [table]);
}
